import React, { useEffect, useState, useMemo } from 'react';
import './Users.css';
import Spinner from './Spinner';
import axios from 'axios';
import DataTable, { DataTableColumn, ActionMenu, DataTableAction } from './DataTable';
import { Icons } from './ActionIcons';

type User = {
  _id?: string;
  username: string;
  fullName?: string;
  password?: string;
  email?: string;
  profile?: string;
  isActive?: boolean;
  isVerified?: boolean;
  loginAttempts?: any;
  preferences?: any;
  createdAt?: string;
  createdBy?: { fullName?: string; username?: string } | string;
  updatedAt?: string;
  modifiedBy?: { fullName?: string; username?: string } | string;
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<User | null>(null); // se setado, mostra o overlay
  const [creating, setCreating] = useState(false);
  const [profiles, setProfiles] = useState<{ _id?: string; name: string }[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<{ [key: string]: string }>({});

  // Resetar página quando filtro mudar
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters]);

  // derived filtered + sorted list (useMemo must be at component top-level)
  const filteredUsers = useMemo(() => {
    let list = users.slice();
    
    // Filtros por coluna
    Object.entries(columnFilters).forEach(([columnKey, filterValue]) => {
      if (filterValue.trim()) {
        const filterLower = filterValue.trim().toLowerCase();
        list = list.filter(u => {
          let cellValue = '';
          
          switch(columnKey) {
            case 'username':
              cellValue = (u.username || '').toLowerCase();
              break;
            case 'fullName':
              cellValue = (u.fullName || '').toLowerCase();
              break;
            case 'email':
              cellValue = (u.email || '').toLowerCase();
              break;
            case 'profile':
              cellValue = (profiles.find(p=>p._id===u.profile)?.name || u.profile || '').toLowerCase();
              break;
            case 'isActive':
              cellValue = u.isActive ? 'sim' : 'não';
              break;
            case 'createdBy':
              cellValue = u.createdBy ? (typeof u.createdBy === 'string' ? u.createdBy : (u.createdBy.fullName || u.createdBy.username || '')).toLowerCase() : '';
              break;
            case 'modifiedBy':
              cellValue = u.modifiedBy ? (typeof u.modifiedBy === 'string' ? u.modifiedBy : (u.modifiedBy.fullName || u.modifiedBy.username || '')).toLowerCase() : '';
              break;
            case 'createdAt':
            case 'updatedAt':
              cellValue = u[columnKey] ? new Date(u[columnKey] as string).toLocaleString().toLowerCase() : '';
              break;
            default:
              cellValue = '';
          }
          
          return cellValue.includes(filterLower);
        });
      }
    });
    
    if (sortColumn) {
      list.sort((a,b) => {
        const getVal = (row:any, c:string) => {
          if (c === 'profile') return profiles.find(p=>p._id===row.profile)?.name || row.profile || '';
          if (c === 'createdBy' || c === 'modifiedBy') return row[c] ? (typeof row[c] === 'string' ? row[c] : (row[c].fullName || row[c].username || '')) : '';
          if (c === 'isActive') return row.isActive ? 1 : 0;
          return row[c] || '';
        };
        const va = getVal(a, sortColumn);
        const vb = getVal(b, sortColumn);
        if ((sortColumn === 'createdAt' || sortColumn === 'updatedAt') && va && vb) {
          return sortDir === 'asc' ? new Date(va).getTime() - new Date(vb).getTime() : new Date(vb).getTime() - new Date(va).getTime();
        }
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return list;
  }, [users, profiles, sortColumn, sortDir, columnFilters]);
  
  // Dados paginados
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, pageSize]);
  
  // in-app notifications / confirm dialog
  const [toast, setToast] = useState<{ visible: boolean; message: string; variant?: 'info' | 'danger' | 'success' }>(
    { visible: false, message: '', variant: 'info' }
  );
  const [confirmState, setConfirmState] = useState<{ visible: boolean; title?: string; message?: string; onConfirm?: () => void }>({ visible: false });
  const [headerOffset, setHeaderOffset] = useState<number>(0);

  

  useEffect(() => {
    // removed: single combined loader below will fetch profiles together with users
  }, []);
  useEffect(() => {
    const base = process.env.REACT_APP_API_URL || '';
    const baseNoSlash = base ? base.replace(/\/$/, '') : '';
    const profilesUrl = baseNoSlash ? `${baseNoSlash}/api/profiles` : '/api/profiles';
    const usersUrl = baseNoSlash ? `${baseNoSlash}/api/users` : '/api/users';
    setLoading(true);
    Promise.all([
      axios.get(profilesUrl, { withCredentials: true }),
      axios.get(usersUrl, { withCredentials: true })
    ])
      .then(([pRes, uRes]) => {
        setProfiles(Array.isArray(pRes.data) ? pRes.data : []);
        setUsers(uRes.data && Array.isArray(uRes.data) ? uRes.data : (uRes.data.users || []));
        setError(null);
      })
      .catch(err => {
        setError(err?.response?.data?.message || err.message || 'Erro ao carregar dados');
        setProfiles([]);
        setUsers([]);
      })
      .finally(() => setLoading(false));
  }, []);

  // compute top offset so page content is not hidden under a top header
  useEffect(() => {
    const updateOffset = () => {
      const headerEl = document.querySelector('.header.fixed-top') as HTMLElement | null;
      const h = headerEl ? Math.ceil(headerEl.getBoundingClientRect().height) : 0;
      setHeaderOffset(h);
    };

    updateOffset();
    window.addEventListener('resize', updateOffset);

    // observe changes to header (layout toggle may change classes/height)
    const headerEl = document.querySelector('.header') as HTMLElement | null;
    let mo: MutationObserver | null = null;
    if (headerEl && 'MutationObserver' in window) {
      mo = new MutationObserver(() => updateOffset());
      mo.observe(headerEl, { attributes: true, childList: false, subtree: false });
    }

    return () => {
      window.removeEventListener('resize', updateOffset);
      if (mo) mo.disconnect();
    };
  }, []);

  const openCreate = () => {
    setEditing({ username: '', fullName: '', email: '', password: '', profile: '', isActive: true });
    setCreating(true);
  };

  const openEdit = (u: User) => {
    // fetch fresh user to include modifiedBy/updatedAt
    const base = process.env.REACT_APP_API_URL || '';
    const url = base ? `${base.replace(/\/$/, '')}/api/users/${u._id || u.username}` : `/api/users/${u._id || u.username}`;
    axios.get(url, { withCredentials: true })
      .then(res => {
        // ensure password field is blank so we don't accidentally overwrite when saving
        const data = res.data || {};
        setEditing({ ...data, password: '' });
      })
      .catch(err => showToast(err?.response?.data?.message || err.message || 'Erro ao carregar usuário', 'danger'));
    setCreating(false);
  };

  const closeOverlay = () => setEditing(null);

  const saveUser = async (u: User) => {
    try {
      const base = process.env.REACT_APP_API_URL || '';
      if (creating) {
        const url = base ? `${base.replace(/\/$/, '')}/api/users` : '/api/users';
        await axios.post(url, u, { withCredentials: true });
      } else {
        const id = u._id || u.username;
        const url = base ? `${base.replace(/\/$/, '')}/api/users/${id}` : `/api/users/${id}`;
        // build minimal updates object: include only fields present/changed
        const allowed = ['fullName', 'email', 'profile', 'password', 'isActive', 'isVerified'];
        const updates: any = {};
        for (const k of allowed) {
          if (Object.prototype.hasOwnProperty.call(u, k)) {
            // only include password if it is non-empty (user actually changed it)
            if (k === 'password') {
              if ((u as any).password && String((u as any).password).length > 0) updates[k] = (u as any)[k];
            } else {
              updates[k] = (u as any)[k];
            }
          }
        }
        await axios.patch(url, updates, { withCredentials: true });
      }
      window.location.reload();
    } catch (err: any) {
      showToast(err?.response?.data?.message || err.message || 'Erro ao salvar', 'danger');
    }
  };

  const showToast = (message: string, variant: 'info' | 'danger' | 'success' = 'info') => {
    setToast({ visible: true, message, variant });
    // auto-hide
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 4000);
  };

  const showConfirm = (title: string, message: string, onConfirm: () => void) => {
  setConfirmState({ visible: true, title, message, onConfirm });
  };

  const hideConfirm = () => setConfirmState({ visible: false });

  // Handle sort
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDir('asc');
    }
  };

  const handleColumnFilter = (columnKey: string, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [columnKey]: value
    }));
  };

  // Função para gerar iniciais do nome
  const getInitials = (fullName: string | undefined): string => {
    if (!fullName) return '?';
    const names = fullName.trim().split(' ').filter(n => n.length > 0);
    if (names.length === 0) return '?';
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  // Função para gerar cor do avatar baseada no nome
  const getAvatarColor = (fullName: string | undefined): string => {
    if (!fullName) return '#6c757d';
    const colors = [
      '#0d6efd', '#6610f2', '#6f42c1', '#d63384', '#dc3545',
      '#fd7e14', '#ffc107', '#198754', '#20c997', '#0dcaf0'
    ];
    const hash = fullName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Define table columns
  const columns: DataTableColumn<User>[] = [
    {
      key: 'username',
      label: 'Usuário',
      sortable: true
    },
    {
      key: 'fullName',
      label: 'Nome',
      sortable: true,
      render: (value: any, row: User) => (
        <div className="user-name-cell">
          <div 
            className="user-avatar" 
            style={{ backgroundColor: getAvatarColor(value) }}
            title={value || ''}
          >
            {getInitials(value)}
          </div>
          <span>{value || ''}</span>
        </div>
      )
    },
    {
      key: 'isActive',
      label: 'Ativo?',
      sortable: true,
      render: (value: any) => value ? 'Sim' : 'Não'
    },
    {
      key: 'email',
      label: 'E-mail',
      sortable: true
    },
    {
      key: 'profile',
      label: 'Perfil',
      sortable: true,
      render: (value: any) => (profiles.find(p => p._id === value)?.name) || value || ''
    },
    {
      key: 'createdAt',
      label: 'Criado em',
      sortable: true,
      render: (value: any) => value ? new Date(value).toLocaleString() : ''
    },
    {
      key: 'createdBy',
      label: 'Criado por',
      sortable: true,
      render: (value: any) => value ? (typeof value === 'string' ? value : (value.fullName || value.username || '')) : ''
    },
    {
      key: 'updatedAt',
      label: 'Modificado em',
      sortable: true,
      render: (value: any) => value ? new Date(value).toLocaleString() : ''
    },
    {
      key: 'modifiedBy',
      label: 'Último modificador',
      sortable: true,
      render: (value: any) => value ? (typeof value === 'string' ? value : (value.fullName || value.username || '')) : ''
    },
    {
      key: 'actions',
      label: 'Ações',
      sortable: false,
      render: (_: any, user: User) => {
        const actions: DataTableAction[] = [
          {
            key: 'edit',
            label: 'Editar',
            icon: Icons.Pencil,
            onClick: () => openEdit(user),
            variant: 'primary'
          },
          {
            key: 'delete',
            label: 'Excluir',
            icon: Icons.Trash,
            onClick: () => {
              showConfirm('Excluir usuário', `Excluir usuário ${user.username}? Essa ação é irreversível.`, async () => {
                const base = process.env.REACT_APP_API_URL || '';
                const url = base ? `${base.replace(/\/$/, '')}/api/users/${user._id || user.username}` : `/api/users/${user._id || user.username}`;
                try {
                  await axios.delete(url, { withCredentials: true });
                  window.location.reload();
                } catch (err: any) {
                  showToast(err?.response?.data?.message || err.message || 'Erro', 'danger');
                }
              });
            },
            variant: 'danger'
          }
        ];
        
        return <ActionMenu actions={actions} />;
      }
    }
  ];

  return (
    <>
      <div className="container py-5" style={{ marginTop: headerOffset ? headerOffset + 12 : 80 }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <div>
              <h1 style={{ marginBottom: 6 }}>Manutenção de Usuários</h1>
              <p className="lead" style={{ marginBottom: 0 }}>Crie, altere e remova usuários do sistema.</p>
            </div>
          </div>
          <div>
            <button 
              className="btn btn-primary" 
              onClick={openCreate} 
              aria-label="Criar usuário"
              style={{ padding: '0.5rem 1.25rem', fontWeight: 600 }}
            >
              Criar usuário
            </button>
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : (
          <DataTable
            columns={columns}
            data={paginatedUsers}
            keyExtractor={(user: User) => user._id || user.username}
            sortColumn={sortColumn}
            sortDirection={sortDir}
            onSort={handleSort}
            onFilter={handleColumnFilter}
            filters={columnFilters}
            striped
            hover
            responsive
            emptyMessage="Nenhum usuário encontrado"
            pagination={true}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={filteredUsers.length}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            pageSizeOptions={[5, 10, 25, 50]}
          />
        )}
        {/* Overlay de criação/edição */}
        {editing && (
          <div className="users-overlay">
            <div className="users-overlay-box">
              <h4>{creating ? 'Criar usuário' : `Editar ${editing.username}`}</h4>
              <div className="mb-2">
                <label className="form-label">Usuário</label>
                <input className="form-control" value={editing.username} onChange={e => setEditing({ ...editing, username: e.target.value })} />
              </div>
                        <div className="mb-2">
                          <label className="form-label">Nome completo</label>
                          <input className="form-control" value={editing.fullName || ''} onChange={e => setEditing({ ...editing, fullName: e.target.value })} />
                        </div>
              <div className="mb-2">
                <label className="form-label">E-mail</label>
                <input className="form-control" value={editing.email || ''} onChange={e => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div className="mb-2">
                <label className="form-label">Perfil</label>
                <select className="form-select" value={editing.profile || ''} onChange={e => setEditing({ ...editing, profile: e.target.value })}>
                  <option value="">-- selecione --</option>
                  {profiles.map(p => (
                    <option key={p._id || p.name} value={p._id || p.name}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label className="form-label">Senha{creating ? '' : ' (deixe em branco para não alterar)'}</label>
                <input
                  className="form-control"
                  type="password"
                  value={editing.password || ''}
                  placeholder={creating ? '' : 'Deixe em branco para manter a senha atual'}
                  autoComplete={creating ? 'new-password' : 'new-password'}
                  onChange={e => setEditing({ ...editing, password: e.target.value })}
                />
              </div>
              <div className="form-check mb-3">
                <input className="form-check-input" type="checkbox" id="isActive" checked={!!editing.isActive} onChange={e => setEditing({ ...editing, isActive: e.target.checked })} />
                <label className="form-check-label" htmlFor="isActive">Ativo</label>
              </div>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={closeOverlay}>Cancelar</button>
                <button className="btn btn-primary" onClick={() => saveUser(editing)}>{creating ? 'Criar' : 'Salvar'}</button>
              </div>
              {/* show last modified info when editing */}
              {!creating && (editing as any)?.modifiedBy && (
                <div className="mt-3 text-muted small">
                  Última modificação: {(editing as any).updatedAt ? new Date((editing as any).updatedAt).toLocaleString() : ''} por {(typeof (editing as any).modifiedBy === 'string' ? (editing as any).modifiedBy : ((editing as any).modifiedBy?.fullName || (editing as any).modifiedBy?.username || ''))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* in-app toast */}
        {toast.visible && (
          <div className={`users-toast users-toast-${toast.variant || 'info'}`} role="status">
            {toast.message}
          </div>
        )}

        {/* in-app confirm modal */}
        {confirmState.visible && (
          <div className="users-confirm-overlay" onClick={hideConfirm}>
            <div className="users-confirm-box" onClick={e => e.stopPropagation()}>
              <h5>{confirmState.title}</h5>
              <p>{confirmState.message}</p>
              <div className="d-flex gap-2 justify-content-end">
                <button className="btn btn-secondary" onClick={hideConfirm}>Cancelar</button>
                <button className="btn btn-danger" onClick={() => { hideConfirm(); confirmState.onConfirm && confirmState.onConfirm(); }}>Confirmar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Users;
